import { isBlocked } from '@nedelnik/shared';

import type { Task, TaskLink } from '@nedelnik/shared';

/**
 * Связи «блокирует» и «зависит от» (3.9).
 *
 * Это одна и та же связь с двух сторон (3.9.1): в базе она хранится в одну
 * сторону (`blocker → blocked`), обратная выводится проходом по тем же записям
 * — решение прототипа, оно же в разделе 3.3 архитектуры. Поэтому здесь нет
 * никакой «второй таблицы»: есть два взгляда на один список.
 */

export interface LinkSide {
  link: TaskLink;
  /** Задача на другом конце связи. `undefined` — её удалили с другого устройства. */
  other: Task | undefined;
}

function sidesOf(
  links: readonly TaskLink[],
  byId: ReadonlyMap<string, Task>,
  mine: (link: TaskLink) => boolean,
  otherId: (link: TaskLink) => string,
): LinkSide[] {
  return links.filter(mine).map((link) => ({ link, other: byId.get(otherId(link)) }));
}

/** Кого держит эта задача: она — `blocker`. */
export function blocks(
  links: readonly TaskLink[],
  taskId: string,
  byId: ReadonlyMap<string, Task>,
): LinkSide[] {
  return sidesOf(
    links,
    byId,
    (link) => link.blocker_id === taskId,
    (link) => link.blocked_id,
  );
}

/** Кто держит эту задачу: она — `blocked`. Это и есть обратный проход. */
export function dependsOn(
  links: readonly TaskLink[],
  taskId: string,
  byId: ReadonlyMap<string, Task>,
): LinkSide[] {
  return sidesOf(
    links,
    byId,
    (link) => link.blocked_id === taskId,
    (link) => link.blocker_id,
  );
}

/**
 * Задачи, которые держит незакрытая зависимость (3.9.4).
 *
 * Считается разом на весь список: `isBlocked` из `shared` отвечает про одну
 * задачу, а метку надо показать в каждой строке — вызов на строку означал бы
 * полный проход по связям на каждую из них.
 */
export function blockedIds(tasks: readonly Task[], links: readonly TaskLink[]): Set<string> {
  const byId = new Map<string, Task>(tasks.map((task) => [task.id, task]));
  const blocked = new Set<string>();

  for (const task of tasks) {
    if (isBlocked(task, links, byId)) blocked.add(task.id);
  }

  return blocked;
}
